import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Copy, Loader2, UserPlus, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { AssessmentData } from '@/pages/CreateAssessment';
import InviteCandidatesDialog from './InviteCandidatesDialog';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useAuth } from '@/components/auth/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface CompletionStepProps {
  assessmentData: AssessmentData;
  onCreateAnother: () => void;
  onCopyLink: () => void;
  onComplete: () => void;
  isSubmitting: boolean;
  insufficientCredits?: boolean;
  onEdit: (step: number) => void;
  assessmentId?: string;
}

const levelText = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior Level'
};

const CompletionStep: React.FC<CompletionStepProps> = ({
  assessmentData,
  onCreateAnother,
  onCopyLink,
  onComplete,
  isSubmitting,
  insufficientCredits,
  onEdit,
  assessmentId
}) => {
  const [isCreated, setIsCreated] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showQuestionsBreakdown, setShowQuestionsBreakdown] = useState(false);
  const { isRecruiterVerified } = useAuth();
  const navigate = useNavigate();
  const isVerified = isRecruiterVerified();

  const handleCreate = async () => {
    try {
      await onComplete();
      // Trigger confetti effect immediately after successful creation
      const titleElement = document.querySelector('h1');
      if (titleElement) {
        const rect = titleElement.getBoundingClientRect();
        const x = (rect.left + rect.width / 2) / window.innerWidth;
        const y = (rect.top + rect.height / 2) / window.innerHeight;
        confetti({
          particleCount: 300,
          spread: 100,
          origin: { x, y },
          gravity: 0.3,
          startVelocity: 45,
          scalar: 0.8,
          ticks: 200,
          decay: 0.94
        });
      }
      setIsCreated(true);
    } catch (error) {
      console.error('Error creating assessment:', error);
    }
  };

  useEffect(() => {
    // If the assessment is already created (e.g., after a page refresh), show confetti
    if (isCreated) {
      const titleElement = document.querySelector('h1');
      if (titleElement) {
        const rect = titleElement.getBoundingClientRect();
        const x = (rect.left + rect.width / 2) / window.innerWidth;
        const y = (rect.top + rect.height / 2) / window.innerHeight;
        confetti({
          particleCount: 300,
          spread: 100,
          origin: { x, y },
          gravity: 0.3,
          startVelocity: 45,
          scalar: 0.8,
          ticks: 200,
          decay: 0.94
        });
      }
    }
  }, [isCreated]);

  const handleInviteClick = () => {
    setShowInviteDialog(true);
  };

  if (isSubmitting && !isCreated) {
    return (
      <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-16 backdrop-blur-sm flex flex-col items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, x: 50 }} 
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute bottom-8 right-8 bg-white rounded-lg p-4 shadow-lg text-[#1A1F2C] border border-[#9b87f5]/30"
        >
          <div className="flex items-center gap-3">
            <Loader2 className="animate-spin text-[#9b87f5]" size={20} />
            <div>
              <p className="font-medium">Creating assessment...</p>
              <p className="text-sm text-[#1A1F2C]/60">Please wait while we generate your assessment.</p>
            </div>
          </div>
        </motion.div>
        
        <div className="flex flex-col items-center gap-8">
          <Loader2 className="animate-spin text-[#9b87f5]" size={50} />
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-3 text-[#1A1F2C]">Creating Your Assessment</h2>
            <p className="text-[#1A1F2C]/70">Please wait while we generate relevant questions for your assessment...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm"
      >
        <div className="flex flex-col items-center">
          <motion.div 
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20,
              delay: 0.3
            }}
            className="w-20 h-20 rounded-full bg-[#EFEAFF] flex items-center justify-center mb-6 shadow-md"
          >
            <CheckCircle className="h-12 w-12 text-[#7662DA]" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]"
          >
            {isCreated ? 'Assessment Created Successfully' : 'Review Assessment Details'}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="text-center text-[#1A1F2C]/80 mb-8"
          >
            {isCreated 
              ? 'Your assessment has been created. You can now invite candidates or create another assessment.'
              : 'Please review the assessment details below and click "Create Assessment" to proceed.'}
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="bg-[#F8F7FF] rounded-lg p-6 w-full mb-8 border border-[#9b87f5]/20"
          >
            <h3 className="text-xl font-semibold mb-4 text-center text-[#1A1F2C]">Assessment Overview</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <div className="text-[#1A1F2C]/70 text-sm">Position</div>
                  {!isCreated && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#7662DA] hover:bg-[#9b87f5]/10 h-7 px-2"
                      onClick={() => onEdit(1)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                <div className="font-medium text-[#1A1F2C]">{levelText[assessmentData.level]} {assessmentData.role}</div>
              </div>
              
              {/* Divider */}
              <div className="w-full h-px bg-[#9b87f5]/10 my-4"></div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <div className="text-[#1A1F2C]/70 text-sm">Selected Skills</div>
                  {!isCreated && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#7662DA] hover:bg-[#9b87f5]/10 h-7 px-2"
                      onClick={() => onEdit(2)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {assessmentData.selectedSkills.map((skill, index) => (
                    <Badge 
                      key={index}
                      className="bg-[#9b87f5]/20 text-[#1A1F2C] hover:bg-[#9b87f5]/30"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Divider */}
              <div className="w-full h-px bg-[#9b87f5]/10 my-4"></div>
              
              {/* Assessment Type Section */}
              {assessmentData.assessmentType && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-[#1A1F2C]/70 text-sm">Assessment Type</div>
                    {!isCreated && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#7662DA] hover:bg-[#9b87f5]/10 h-7 px-2"
                        onClick={() => onEdit(2)}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                  <div className="text-[#1A1F2C]/80">
                    <Badge 
                      className={`px-3 py-1 ${
                        assessmentData.assessmentType === 'customized' 
                          ? "bg-blue-100 text-blue-800 border-blue-300" 
                          : "bg-purple-100 text-purple-800 border-purple-300"
                      }`}
                    >
                      {assessmentData.assessmentType === 'customized' ? 'Customized' : 'AI-Assisted'}
                    </Badge>
                  </div>
                </div>
              )}
              
              {/* Divider */}
              <div className="w-full h-px bg-[#9b87f5]/10 my-4"></div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <div className="text-[#1A1F2C]/70 text-sm">Questions</div>
                  {!isCreated && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#7662DA] hover:bg-[#9b87f5]/10 h-7 px-2"
                      onClick={() => onEdit(3)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                
                {/* Questions summary */}
                <div className="text-[#1A1F2C]/80 mb-3">
                  <span className="font-semibold text-[#1A1F2C]">
                    Total: {assessmentData.customQuestions.length + (
                      assessmentData.skillQuestions 
                        ? Object.values(assessmentData.skillQuestions).flat().filter(q => q.trim() !== '').length 
                        : 0
                    )} questions
                  </span>
                </div>
                
                {/* Questions breakdown */}
                <div className="space-y-2 pl-3 border-l-2 border-[#9b87f5]/20 mt-3 mb-1">
                  <div className="text-sm flex justify-between items-center">
                    <span className="font-medium text-[#1A1F2C]">General Questions:</span>
                    <Badge className="bg-[#9b87f5]/20 text-[#1A1F2C] hover:bg-[#9b87f5]/30 ml-2 px-2 py-0.5 min-w-[30px] text-center">
                      {assessmentData.customQuestions.length}
                    </Badge>
                  </div>
                  
                  {assessmentData.skillQuestions && Object.entries(assessmentData.skillQuestions).map(([skill, questions]) => {
                    const filteredQuestions = questions.filter(q => q.trim() !== '');
                    if (filteredQuestions.length === 0) return null;
                    
                    return (
                      <div key={skill} className="text-sm flex justify-between items-center">
                        <span className="font-medium text-[#1A1F2C]/90 mr-2">{skill}:</span>
                        <Badge className="bg-[#9b87f5]/20 text-[#1A1F2C] hover:bg-[#9b87f5]/30 px-2 py-0.5 min-w-[30px] text-center">
                          {filteredQuestions.length}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Divider */}
              <div className="w-full h-px bg-[#9b87f5]/10 my-4"></div>
              
              {/* Interviewer Settings section */}
              {assessmentData.language && assessmentData.accent && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-[#1A1F2C]/70 text-sm">Interviewer Settings</div>
                    {!isCreated && assessmentData.assessmentType === 'customized' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#7662DA] hover:bg-[#9b87f5]/10 h-7 px-2"
                        onClick={() => onEdit(4)}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                  <div className="text-[#1A1F2C]/80">
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Badge 
                        variant="outline" 
                        className="bg-[#9b87f5]/10 text-[#7662DA] border-[#9b87f5]/30 px-3 py-1"
                      >
                        Language: {assessmentData.language}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className="bg-[#9b87f5]/10 text-[#7662DA] border-[#9b87f5]/30 px-3 py-1"
                      >
                        Accent: {assessmentData.accent}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={`px-3 py-1 ${
                          assessmentData.voiceId 
                            ? "bg-green-100 text-green-800 border-green-300" 
                            : "bg-amber-100 text-amber-800 border-amber-300"
                        }`}
                      >
                        Voice: {assessmentData.voiceId ? "Configured" : "Not configured"}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
              
              {insufficientCredits && !isCreated && (
                <>
                  {/* Divider */}
                  <div className="w-full h-px bg-[#9b87f5]/10 my-4"></div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle size={18} />
                      <span className="font-medium">Insufficient Credits</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      You need at least 1 credit to create and activate this assessment.
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.5 }}
            className="flex justify-center w-full mb-4"
          >
            {!isCreated ? (
              <Button 
                variant="purple"
                className="w-64 bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
                onClick={handleCreate}
                disabled={isSubmitting || insufficientCredits}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" /> Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" /> Create Assessment
                  </>
                )}
              </Button>
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 w-full"
              >
                <Button 
                  variant="light-outline" 
                  className="flex-1"
                  onClick={handleInviteClick}
                >
                  <UserPlus size={16} className="mr-2" /> Invite Candidates
                </Button>
                
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.location.href = `/assessments/${assessmentId}`}
                >
                  Go to Assessment
                </Button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.div>

      <InviteCandidatesDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onCopyLink={onCopyLink}
        assessmentId={assessmentId}
        assessmentTitle={`${levelText[assessmentData.level]} ${assessmentData.role}`}
      />
    </>
  );
};

export default CompletionStep;
